import { createClient, type Session } from '@supabase/supabase-js';
import { safeGet } from './storage';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '').trim();
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();

export const supabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

// When env vars are missing (local dev without secrets), expose a stub so
// imports don't crash. Auth UI guards against this with `supabaseConfigured`.
export const supabase = supabaseConfigured
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        detectSessionInUrl: true,
        autoRefreshToken: true,
        flowType: 'pkce',
        storageKey: 'mela.auth',
      },
    })
  : (null as never);

// ---- Access-token cache ----
// The api-client interface wants a synchronous getAccessToken(). Supabase JS
// gives us the session asynchronously, so cache the access_token on every
// auth-state change and serve the cached value to api-client.

let cachedAccessToken: string | null = null;

if (supabaseConfigured) {
  // Hydrate from storage immediately so the first render has a token.
  const raw = safeGet('mela.auth');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { access_token?: string; expires_at?: number };
      if (parsed.access_token && (!parsed.expires_at || parsed.expires_at * 1000 > Date.now())) {
        cachedAccessToken = parsed.access_token;
      }
    } catch {
      /* storage corrupt — ignore */
    }
  }

  // Refresh on every auth state change (sign in, sign out, token refresh).
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedAccessToken = session?.access_token ?? null;
  });

  // Also resolve once on boot in case the in-storage token is stale.
  supabase.auth.getSession().then(({ data }) => {
    cachedAccessToken = data.session?.access_token ?? null;
  });
}

export function getAccessToken(): string | null {
  return cachedAccessToken;
}

export type { Session };
